import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from '../../../store';
import {
    applyCopySnapshot,
    clearDraft,
    hydrateDraft,
    markDraftSaved,
    setDraftId,
    setDraftIdentity,
    useConfigTemplateDraftMeta,
} from '../../../store/slices/configTemplateDraftSlice';
import {
    copyConfigTemplateFromLive,
    createConfigTemplate,
    getConfigTemplateDetails,
    getFieldSchema,
    parseConfigTemplateError,
    updateConfigTemplate,
} from '../../../services/configTemplateService';
import type {
    ConfigTemplateDraft,
    ConfigTemplateFieldSchemaResponse,
    ConfigTemplateIdentity,
    ConfigTemplateLevel,
} from '../../../types/configTemplate.types';
import { suggestedCopyName } from '../configTemplateCopy';
import {
    emptyDraft,
    flattenSchemaFields,
    identityFromDocument,
    includedAndValuesFromTrees,
    serializeDraft,
    toCreateBody,
    toUpdateBody,
    treesFromDocument,
} from '../utils/draftPayload';

function storageKey(level: ConfigTemplateLevel, id: string | null): string {
    return `config-template-draft:${level}:${id ?? 'new'}`;
}

function readStored(level: ConfigTemplateLevel, id: string | null): ConfigTemplateDraft | null {
    try {
        const raw = localStorage.getItem(storageKey(level, id));
        if (!raw) return null;
        return JSON.parse(raw) as ConfigTemplateDraft;
    } catch {
        return null;
    }
}

function writeStored(draft: ConfigTemplateDraft) {
    localStorage.setItem(storageKey(draft.level, draft.id), serializeDraft(draft));
}

function clearStored(level: ConfigTemplateLevel, id: string | null) {
    localStorage.removeItem(storageKey(level, id));
    if (id) localStorage.removeItem(storageKey(level, null));
}

export function useTemplateEditorDraft(args: {
    level: ConfigTemplateLevel;
    id: string | null;
    duplicateFrom: string | null;
}) {
    const dispatch = useAppDispatch();
    const { draft, lastSaved } = useConfigTemplateDraftMeta();
    const [schema, setSchema] = useState<ConfigTemplateFieldSchemaResponse | null>(null);
    const [schemaStatus, setSchemaStatus] = useState<'idle' | 'loading' | 'success' | 'error'>(
        'idle'
    );
    const [bootStatus, setBootStatus] = useState<'loading' | 'ready' | 'error'>('loading');
    const [saveError, setSaveError] = useState<'name' | 'clash' | 'request' | null>(null);
    const [saving, setSaving] = useState(false);
    const [copyFail, setCopyFail] = useState<{ id: string; name: string } | null>(null);
    const [copyRetrying, setCopyRetrying] = useState(false);
    const [savedFlash, setSavedFlash] = useState(false);
    const hydratedKey = useRef<string>('');

    const fields = useMemo(() => flattenSchemaFields(schema), [schema]);

    const loadSchema = useCallback(async () => {
        setSchemaStatus('loading');
        try {
            const data = await getFieldSchema({ level: args.level });
            setSchema(data);
            setSchemaStatus('success');
            return data;
        } catch {
            setSchemaStatus('error');
            return null;
        }
    }, [args.level]);

    const boot = useCallback(async () => {
        setBootStatus('loading');
        setSaveError(null);
        setCopyFail(null);
        const key = `${args.level}:${args.id ?? 'new'}:${args.duplicateFrom ?? ''}`;
        hydratedKey.current = key;
        try {
            const schemaData = await loadSchema();
            const schemaFields = flattenSchemaFields(schemaData);

            const storedForId = readStored(args.level, args.id);
            if (storedForId && storedForId.level === args.level) {
                dispatch(
                    hydrateDraft({
                        draft: storedForId,
                        lastSaved: args.id ? serializeDraft(storedForId) : null,
                    })
                );
                setBootStatus('ready');
                return;
            }

            if (args.id) {
                const doc = await getConfigTemplateDetails({ id: args.id });
                const next: ConfigTemplateDraft = {
                    level: args.level,
                    id: doc._id,
                    identity: identityFromDocument(doc),
                    ...includedAndValuesFromTrees(treesFromDocument(doc), schemaFields),
                    copyBaseline: null,
                };
                dispatch(hydrateDraft({ draft: next, lastSaved: serializeDraft(next) }));
                setBootStatus('ready');
                return;
            }

            if (args.duplicateFrom) {
                const doc = await getConfigTemplateDetails({ id: args.duplicateFrom });
                const next: ConfigTemplateDraft = {
                    level: args.level,
                    id: null,
                    identity: {
                        ...identityFromDocument(doc),
                        name: suggestedCopyName(doc.name),
                    },
                    ...includedAndValuesFromTrees(treesFromDocument(doc), schemaFields),
                    copyBaseline: null,
                };
                dispatch(hydrateDraft({ draft: next, lastSaved: null }));
                setBootStatus('ready');
                return;
            }

            const storedNew = readStored(args.level, null);
            dispatch(
                hydrateDraft({
                    draft:
                        storedNew && storedNew.level === args.level
                            ? storedNew
                            : emptyDraft(args.level),
                    lastSaved: null,
                })
            );
            setBootStatus('ready');
        } catch {
            setBootStatus('error');
        }
    }, [args.duplicateFrom, args.id, args.level, dispatch, loadSchema]);

    useEffect(() => {
        void boot();
        return () => {
            dispatch(clearDraft());
        };
    }, [boot, dispatch]);

    useEffect(() => {
        if (!draft || bootStatus !== 'ready') return;
        writeStored(draft);
    }, [draft, bootStatus]);

    const dirty = useMemo(() => {
        if (!draft) return false;
        const current = serializeDraft(draft);
        if (lastSaved) return current !== lastSaved;
        const blank = serializeDraft(emptyDraft(draft.level));
        return current !== blank;
    }, [draft, lastSaved]);

    const updateIdentity = useCallback(
        (patch: Partial<ConfigTemplateIdentity>) => {
            dispatch(setDraftIdentity(patch));
        },
        [dispatch]
    );

    const save = useCallback(async () => {
        if (!draft) return null;
        if (!draft.identity.name.trim()) {
            setSaveError('name');
            return null;
        }
        setSaving(true);
        setSaveError(null);
        try {
            const saved = draft.id
                ? await updateConfigTemplate(toUpdateBody(draft))
                : await createConfigTemplate(toCreateBody(draft));
            const next: ConfigTemplateDraft = {
                ...draft,
                id: saved._id,
                identity: {
                    ...draft.identity,
                    name: saved.name,
                },
            };
            const serialized = serializeDraft(next);
            clearStored(draft.level, draft.id);
            writeStored(next);
            dispatch(setDraftId(saved._id));
            dispatch(markDraftSaved(serialized));
            setSavedFlash(true);
            window.setTimeout(() => setSavedFlash(false), 1600);
            setSaving(false);
            return saved;
        } catch (err: unknown) {
            setSaveError(parseConfigTemplateError(err).isNameClash ? 'clash' : 'request');
            setSaving(false);
            return null;
        }
    }, [dispatch, draft]);

    const discard = useCallback(() => {
        if (draft) clearStored(draft.level, draft.id);
        dispatch(clearDraft());
    }, [dispatch, draft]);

    const clearSaveError = useCallback(() => setSaveError(null), []);
    const markCopyFailed = useCallback((source: { id: string; name: string }) => {
        setCopyFail(source);
    }, []);
    const clearCopyFail = useCallback(() => setCopyFail(null), []);

    const retryCopy = useCallback(async () => {
        if (!copyFail || !draft) return false;
        setCopyRetrying(true);
        try {
            const snapshot = await copyConfigTemplateFromLive({
                level: args.level,
                storeBrandId: args.level === 'STORE_BRAND' ? copyFail.id : undefined,
                storeId: args.level === 'STORE' ? copyFail.id : undefined,
                templateId: draft.id ?? undefined,
            });
            const mapped = includedAndValuesFromTrees(snapshot, fields);
            dispatch(
                applyCopySnapshot({
                    included: mapped.included,
                    values: mapped.values,
                    sourceName: copyFail.name,
                })
            );
            setCopyFail(null);
            setCopyRetrying(false);
            return true;
        } catch {
            setCopyRetrying(false);
            return false;
        }
    }, [args.level, copyFail, dispatch, draft, fields]);

    return {
        draft,
        fields,
        schema,
        schemaStatus,
        bootStatus,
        retryBoot: boot,
        retrySchema: loadSchema,
        dirty,
        saving,
        saveError,
        clearSaveError,
        savedFlash,
        updateIdentity,
        save,
        discard,
        copyFail,
        copyRetrying,
        markCopyFailed,
        clearCopyFail,
        retryCopy,
    };
}
